import type { PreloadApiManagerApiCallback, PreloadApiManagerData, PreloadApiManagerOptions } from './types';

/**
 * Manages preloading, temporarily storing, and retrieving API promises
 * Should be used to enhance the loading speed of API calls made by lazy-loaded javascript
 */
export class PreloadApiManager {
	readonly data: PreloadApiManagerData = new Map();
	readonly STALE_TIME_MS = 10_000;
	private readonly apiCallback: PreloadApiManagerApiCallback;

	constructor({ apiCallback }: PreloadApiManagerOptions) {
		this.apiCallback = apiCallback;
	}

	/**
	 * Preloads API and stores its promise for STALE_TIME_MS
	 */
	preload(url: string) {
		const promise = this.apiCallback(url);

		this.data.set(url, promise);
		setTimeout(() => {
			this.data.has(url) && this.data.delete(url);
		}, this.STALE_TIME_MS);
	}

	/**
	 * Retrieves promise of preloaded API call and deletes it permanently
	 */
	pull<T>(url: string): undefined | Promise<T> {
		const preloadedApi = this.data.get(url);
		preloadedApi && this.data.delete(url);

		return preloadedApi;
	}
}
